import i18next from 'i18next'
import React, { Component } from 'react'
import { Link } from 'widgets-for-react'
import { ButtonPrimary } from '../../../../utils/buttons'
import { dietaryWishes } from '../../../../utils/dietaryWishesAndAllergens'
import {
  AuthButtonPrimary,
  AuthButtonSecondary,
  FormControls,
} from '../../../../utils/forms'
import {
  ComponentDidMount,
  ComponentWillUnmount,
} from '../../../../utils/lifecycleMethods'
import { ModalLayout } from '../../../../utils/modal'
import { SVGIcon } from '../../../../utils/svgicon'
import { capitalize, slugify } from '../../../../utils/utils'
import { ViewListenerState } from '../../viewListener/viewListenerState'
import { Filter } from '../filtersLoader/filtersLoaderState'
import { ModalState } from './topFiltersState'

type TopFilter = {
  items: TopFilterItem[]
  toggled: boolean
  toggle: () => void
  reset: { setState: () => void; disable: boolean }
  save: { url: string; current: boolean }
  filterCount: number
}

type TopFilterItem = {
  label: Filter
  isChecked: boolean
  onCheck: () => void
}

export const TopFiltersLayout = (props: {
  view: ViewListenerState
  techniques: TopFilter
  appearances: TopFilter
  textures: TopFilter
  allergens: TopFilter
  types: TopFilter
  resultsCount?: number
  modalState: ModalState
  setModalState: (modalState: ModalState) => void
  saveAll: { url: string; current: boolean }
  resetAll: { url: string; hide: boolean }
}) =>
  props.view === 'mobile' ? (
    <TopFiltersMobileWrapper>
      <TopFiltersMobileButton
        onToggleModal={() =>
          props.modalState === 'closed'
            ? props.setModalState('pick filter')
            : props.setModalState('closed')
        }
        resultsCount={props.resultsCount}
      />
      {props.modalState !== 'closed' && (
        <ModalLayout
          onHide={() => props.setModalState('closed')}
          title={i18next.t('componentFilter:topFiltersTitle')}
          extraHeaderJSX={
            props.modalState != 'pick filter' ? (
              <button
                type="button"
                className="link--inverted-grey text--s"
                onClick={() => props.setModalState('pick filter')}
              >
                &#8249; {i18next.t('auth:goBack')}
              </button>
            ) : undefined
          }
          primaryAction={
            props.modalState === 'pick filter'
              ? {
                  kind: 'link',
                  label: i18next.t('componentFilter:showComponents'),
                  to: props.saveAll.url,
                  isDisabled: props.saveAll.current,
                  onClick: () => props.setModalState('closed'),
                }
              : {
                  kind: 'button',
                  label: i18next.t('common:save'),
                  onClick: () => props.setModalState('pick filter'),
                }
          }
          secondaryAction={
            props.modalState === 'pick filter'
              ? {
                  kind: 'link',
                  label: i18next.t('componentFilter:resetAllFilters'),
                  to: props.resetAll.url,
                  onClick: () => props.setModalState('closed'),
                }
              : {
                  kind: 'button',
                  label: i18next.t('componentFilter:resetFilter'),
                  onClick: (() => {
                    switch (props.modalState) {
                      case 'allergens':
                        return props.allergens.reset.setState
                      case 'techniques':
                        return props.techniques.reset.setState
                      case 'types':
                        return props.types.reset.setState
                    }
                  })(),
                }
          }
        >
          <MobileTopFiltersModalLayout
            modalState={props.modalState}
            setModalState={props.setModalState}
            techniques={props.techniques}
            appearances={props.appearances}
            textures={props.textures}
            allergens={props.allergens}
            types={props.types}
          />
        </ModalLayout>
      )}
    </TopFiltersMobileWrapper>
  ) : (
    <TopFiltersDesktopLayout
      techniques={props.techniques}
      appearances={props.appearances}
      textures={props.textures}
      allergens={props.allergens}
      types={props.types}
      resetTopFilters={props.resetAll}
    />
  )

// ----- Mobile

const TopFiltersMobileButton = (p: {
  resultsCount?: number
  onToggleModal: () => void
}) => (
  <ButtonPrimary
    kind="button"
    size="m"
    iconId="filter"
    iconSize="20"
    label={i18next.t('componentFilter:filterXResults', {
      count: p.resultsCount,
    })}
    onClick={p.onToggleModal}
    extraClasses="top-filter__modal"
  />
)

// Top filter 'inverted' translation
// E.g. 'No Egg', 'Geen Gluten' or 'Vegetarian'
const invertAllergenTranslation = (allergen: Filter) => {
  // Check first if allergen is a dietary wish, if so render dietary wish translation
  // Otherwise add `No ` prefix
  return dietaryWishes.includes(allergen.Title)
    ? i18next.t(`component:${slugify(allergen.Title)}`)
    : i18next.t(`component:noEntity`, {
        entity: allergen.Translation.toLowerCase(),
      })
}

const MobileTopFiltersModalLayout = (props: {
  modalState: ModalState
  setModalState: (state: ModalState) => void
  techniques: TopFilter
  appearances: TopFilter
  textures: TopFilter
  allergens: TopFilter
  types: TopFilter
}) => {
  return props.modalState === 'pick filter' ? (
    // Step 1
    <ul className="top-filter__list list--group">
      {/* Types */}
      <li className="list__item">
        <MobileModalButtonLayout
          label={i18next.t('componentFilter:type')}
          onClick={() => props.setModalState('types')}
          activeFilters={props.types.items
            .filter((i) => i.isChecked)
            .map((i) => i.label.Translation)}
        />
      </li>
      {/* Allergens */}
      <li className="list__item">
        <MobileModalButtonLayout
          label={i18next.t('componentFilter:allergens')}
          onClick={() => props.setModalState('allergens')}
          activeFilters={props.allergens.items
            .filter((i) => i.isChecked)
            .map((i) => invertAllergenTranslation(i.label))}
        />
      </li>
      {/* Techniques */}
      <li className="list__item">
        <MobileModalButtonLayout
          label={i18next.t('componentFilter:techniques')}
          onClick={() => props.setModalState('techniques')}
          activeFilters={props.techniques.items
            .filter((i) => i.isChecked)
            .map((i) => i.label.Translation)}
        />
      </li>
      {/* Appearances */}
      <li className="list__item">
        <MobileModalButtonLayout
          label={i18next.t('componentFilter:appearances')}
          onClick={() => props.setModalState('appearances')}
          activeFilters={props.appearances.items
            .filter((i) => i.isChecked)
            .map((i) => i.label.Translation)}
        />
      </li>
      {/* Textures */}
      <li className="list__item">
        <MobileModalButtonLayout
          label={i18next.t('componentFilter:textures')}
          onClick={() => props.setModalState('textures')}
          activeFilters={props.textures.items
            .filter((i) => i.isChecked)
            .map((i) => i.label.Translation)}
        />
      </li>
    </ul>
  ) : (
    // Step 2
    <>
      {(() => {
        switch (props.modalState) {
          case 'techniques':
            return props.techniques.items
              .sort((fst, snd) =>
                fst.label.Translation.localeCompare(snd.label.Translation)
              )
              .map((item) => (
                <FilterCheckboxLayout
                  key={`t-${item.label.Title}`}
                  kind="techniques"
                  {...item}
                />
              ))

          case 'appearances':
            return props.appearances.items
              .sort((fst, snd) =>
                fst.label.Translation.localeCompare(snd.label.Translation)
              )
              .map((item) => (
                <FilterCheckboxLayout
                  key={`t-${item.label.Title}`}
                  kind="appearances"
                  {...item}
                />
              ))

          case 'textures':
            return props.textures.items
              .sort((fst, snd) =>
                fst.label.Translation.localeCompare(snd.label.Translation)
              )
              .map((item) => (
                <FilterCheckboxLayout
                  key={`t-${item.label.Title}`}
                  kind="textures"
                  {...item}
                />
              ))

          case 'allergens':
            return props.allergens.items
              .sort((fst, snd) =>
                invertAllergenTranslation(fst.label).localeCompare(
                  invertAllergenTranslation(snd.label)
                )
              )
              .map((item) => (
                <FilterCheckboxLayout
                  key={`a-${item.label.Title}`}
                  kind="allergens"
                  {...item}
                />
              ))

          case 'types':
            return props.types.items
              .sort((fst, snd) =>
                fst.label.Translation.localeCompare(snd.label.Translation)
              )
              .map((item) => (
                <FilterCheckboxLayout
                  key={`t-${item.label.Title}`}
                  kind="types"
                  {...item}
                />
              ))
        }
      })()}
    </>
  )
}

const MobileModalButtonLayout = (p: {
  label: string
  onClick: () => void
  activeFilters: string[]
}) => (
  <button type="button" className="list__link" onClick={p.onClick}>
    <div className="list__content">
      <span className="list__title">{p.label}</span>
      {p.activeFilters.length > 0 && (
        <ul className="list__subtitle">
          {p.activeFilters.map((component) => (
            <li
              className="text--xs"
              key={component}
              dangerouslySetInnerHTML={{ __html: component }}
            />
          ))}
        </ul>
      )}
    </div>
    <SVGIcon className="list__icon" size={12} iconId="chevron-right" />
  </button>
)

const TopFiltersMobileWrapper = (p: { children: React.ReactNode }) => {
  return (
    <section className="c-filter__top top-filter top-filter--mobile">
      {p.children}
    </section>
  )
}

// ----- Desktop

type TopFiltersDesktopProps = {
  techniques: TopFilter
  appearances: TopFilter
  textures: TopFilter
  allergens: TopFilter
  types: TopFilter
  resetTopFilters: { url: string; hide: boolean }
}

class TopFiltersDesktopLayout extends Component<TopFiltersDesktopProps> {
  constructor(props: TopFiltersDesktopProps) {
    super(props)
    this.techniquesRef = React.createRef()
    this.appearancesRef = React.createRef()
    this.texturesRef = React.createRef()
    this.allergensRef = React.createRef()
    this.typesRef = React.createRef()
    this.handleClickOutside = this.handleClickOutside.bind(this)
  }

  techniquesRef: React.RefObject<HTMLLIElement>
  appearancesRef: React.RefObject<HTMLLIElement>
  texturesRef: React.RefObject<HTMLLIElement>
  allergensRef: React.RefObject<HTMLLIElement>
  typesRef: React.RefObject<HTMLLIElement>

  handleClickOutside = (event: MouseEvent) => {
    if (event.target !== null) {
      if (
        this.techniquesRef.current !== null &&
        !this.techniquesRef.current.contains(event?.target as Node) &&
        this.props.techniques.toggled
      )
        this.props.techniques.toggle()

      if (
        this.appearancesRef.current !== null &&
        !this.appearancesRef.current.contains(event?.target as Node) &&
        this.props.appearances.toggled
      )
        this.props.appearances.toggle()

      if (
        this.texturesRef.current !== null &&
        !this.texturesRef.current.contains(event?.target as Node) &&
        this.props.textures.toggled
      )
        this.props.textures.toggle()

      if (
        this.allergensRef.current !== null &&
        !this.allergensRef.current.contains(event?.target as Node) &&
        this.props.allergens.toggled
      )
        this.props.allergens.toggle()

      if (
        this.typesRef.current !== null &&
        !this.typesRef.current.contains(event?.target as Node) &&
        this.props.types.toggled
      )
        this.props.types.toggle()
    }
  }

  render() {
    const {
      allergens,
      techniques,
      appearances,
      textures,
      types,
      resetTopFilters,
    } = this.props
    return (
      <>
        <ComponentDidMount
          onMount={() =>
            document.addEventListener('mousedown', this.handleClickOutside)
          }
        />
        <ComponentWillUnmount
          onUnmount={() =>
            document.removeEventListener('mousedown', this.handleClickOutside)
          }
        />
        <section className="c-filter__top top-filter top-filter--desktop">
          <h2 className="sr-text">
            {i18next.t('componentFilter:topFiltersTitle')}
          </h2>

          <ul className="top-filter__list">
            {/* Types */}
            <li ref={this.typesRef} className="top-filter__item">
              <DesktopFilterDropdownLayout
                kind="types"
                label={i18next.t('componentFilter:type')}
                filter={types}
              />
            </li>
            {/* Allergens */}
            <li ref={this.allergensRef} className="top-filter__item">
              <DesktopFilterDropdownLayout
                kind="allergens"
                label={i18next.t('componentFilter:allergens')}
                filter={allergens}
              />
            </li>
            {/* Techniques */}
            <li ref={this.techniquesRef} className="top-filter__item">
              <DesktopFilterDropdownLayout
                kind="techniques"
                label={i18next.t('componentFilter:techniques')}
                filter={techniques}
              />
            </li>
            {/* Appearances */}
            <li ref={this.appearancesRef} className="top-filter__item">
              <DesktopFilterDropdownLayout
                kind="appearances"
                label={i18next.t('componentFilter:appearances')}
                filter={appearances}
              />
            </li>
            {/* Textures */}
            <li ref={this.texturesRef} className="top-filter__item">
              <DesktopFilterDropdownLayout
                kind="textures"
                label={i18next.t('componentFilter:textures')}
                filter={textures}
              />
            </li>
            {!resetTopFilters.hide && (
              <li className="top-filter__item">
                <Link
                  to={resetTopFilters.url}
                  className="link--inverted text--s"
                >
                  {i18next.t('componentFilter:resetAllFilters')}
                </Link>
              </li>
            )}
          </ul>
        </section>
      </>
    )
  }
}

const DesktopFilterDropdownLayout = (p: {
  label: string
  filter: TopFilter
  kind: 'techniques' | 'appearances' | 'textures' | 'allergens' | 'types'
}) => {
  const array = (() => {
    switch (p.kind) {
      case 'allergens':
        return p.filter.items.sort((fst, snd) =>
          invertAllergenTranslation(fst.label).localeCompare(
            invertAllergenTranslation(snd.label)
          )
        )
      case 'techniques':
      case 'appearances':
      case 'textures':
      case 'types':
        return p.filter.items.sort((fst, snd) =>
          fst.label.Translation.localeCompare(snd.label.Translation)
        )
    }
  })()

  return (
    <fieldset className="top-filter__dropdown expand expand--filter">
      <legend className="sd__selector">
        <button
          type="button"
          className={`expand__button${
            p.filter.filterCount > 0 ? ' expand__button--active' : ''
          }`}
          onClick={p.filter.toggle}
          aria-expanded={p.filter.toggled}
          aria-controls={`a11y-sd-${slugify(p.label)}`}
        >
          <span className="button__label">
            {p.filter.filterCount
              ? `${p.label} (${p.filter.filterCount})`
              : p.label}
          </span>
          <SVGIcon className="expand__toggle" size={10} iconId="chevron-down" />
        </button>
      </legend>

      <div
        id={`a11y-filter-${slugify(p.label)}`}
        aria-hidden={!p.filter.toggled}
        className="expand__sub"
      >
        <div className="expand__content text--m">
          {array.map((item) => (
            <FilterCheckboxLayout
              key={`ec-${item.label.Title}`}
              kind={p.kind}
              {...item}
            />
          ))}
        </div>

        <div className="expand__footer">
          <AuthButtonSecondary
            kind="button"
            onClick={p.filter.reset.setState}
            disabled={p.filter.reset.disable}
            label={i18next.t('common:reset')}
          />
          <AuthButtonPrimary
            kind="link"
            to={p.filter.save.url}
            disabled={p.filter.save.current}
            label={i18next.t('common:save')}
          />
        </div>
      </div>
    </fieldset>
  )
}

const FilterCheckboxLayout = (
  p: TopFilterItem & {
    kind: 'techniques' | 'appearances' | 'textures' | 'allergens' | 'types'
  }
) => {
  return (
    <FormControls.FormCheckInGroup
      controlId={`sd-item-${slugify(p.label.Title)}`}
      label={(() => {
        switch (p.kind) {
          case 'allergens':
            return invertAllergenTranslation(p.label)
          case 'appearances':
          case 'textures':
            return capitalize(p.label.Translation)
          case 'techniques':
          case 'types':
            return p.label.Translation
        }
      })()}
      required={false}
      checked={p.isChecked}
      onChange={p.onCheck}
    />
  )
}
